// GYM-900 / GYM-479b / GYM-1048: React hook — flushes the write queue when the
// app comes back online (foreground fallback for browsers without Background Sync).
// Also exposes queue length so the UI can show a "X pending" badge.
//
// Token source is pluggable by audience:
//   "staff"  → useAuthStore (staff JWT)
//   "member" → useMemberAuthStore (member JWT)
// This is CRITICAL — before GYM-1048 the hook always read the staff store,
// meaning any queued member write would replay with the wrong/absent token.

import { useCallback, useEffect, useState } from "react";

import { useAuthStore } from "@/lib/auth/store";
import { useMemberAuthStore } from "@/lib/auth/memberStore";
import { flushQueue } from "./syncQueue";
import { listQueue } from "./writeQueue";

export function useOfflineSync() {
  const staffAccess = useAuthStore((s) => s.access);
  const memberAccess = useMemberAuthStore((s) => s.access);
  const [pendingCount, setPendingCount] = useState(0);

  const getToken = useCallback((audience: "staff" | "member") => {
    if (audience === "member") return useMemberAuthStore.getState().access;
    return useAuthStore.getState().access;
  }, []);

  const refresh = useCallback(async () => {
    const entries = await listQueue();
    setPendingCount(entries.length);
  }, []);

  const flush = useCallback(async () => {
    await flushQueue(getToken);
    await refresh();
  }, [getToken, refresh]);

  useEffect(() => {
    void refresh();

    const handleOnline = () => { void flush(); };
    window.addEventListener("online", handleOnline);
    return () => window.removeEventListener("online", handleOnline);
  }, [staffAccess, memberAccess, flush, refresh]);

  return { pendingCount, flush, refresh };
}
